import { useMemo } from 'react'

import type { AccessAction } from '@/config/access-control'
import type { Permissions } from '@/rbac/grants'
import { isPermissionGrantActive } from '@/rbac/isPermissionGrantActive'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { getViewGrantScope, viewGrantAllowsModule } from '@/utils/access-control/grantScope'

export type RbacResult = { allowed: true } | { allowed: false; reason: 'no-permission' }

/**
 * Thin RBAC primitive that composes into useAccessControl.
 * Returns `{ allowed: true }` when no permission is specified (caller didn't gate on RBAC).
 *
 * For `view`, grants are evaluated with list/filter semantics (`'all'`, `{ some: [...] }`, etc.)
 * instead of truthy object checks.
 */
export function useRbac(permission?: keyof Permissions, action: AccessAction = 'create'): RbacResult {
	const user = useAuthStore(s => s.user)
	return useMemo((): RbacResult => {
		if (!permission) {
			return { allowed: true }
		}
		const grant = isPlatformUser(user) ? user.role?.permissions?.[permission] : undefined
		const allowed = action === 'view' ? viewGrantAllowsModule(grant) : isPermissionGrantActive(grant)
		return allowed ? { allowed: true } : { allowed: false, reason: 'no-permission' }
	}, [action, permission, user])
}

export { getViewGrantScope }
